// src/components/RecommenderSection.js
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { getFieldOwnerLabel } from "../lib/fieldAccess";
import api from "../lib/api";
import useMyFields from "../hooks/useMyFields";
import LazySyncedMetricCharts from "./LazySyncedMetricCharts";

const fieldSelectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: "#fff",
      border: "1px solid #e8e8e8",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
      "& .MuiMenuItem-root": {
        backgroundColor: "#fff !important",
        outline: "none !important",
        boxShadow: "none !important",
        "&:hover, &:focus, &:focus-visible, &.Mui-focusVisible": {
          backgroundColor: "#fffaf0 !important",
          outline: "none !important",
          boxShadow: "none !important",
        },
        "&.Mui-selected": { backgroundColor: "#fff7d6 !important" },
        "&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected:focus-visible, &.Mui-selected.Mui-focusVisible": {
          backgroundColor: "#ffefb0 !important",
          outline: "none !important",
          boxShadow: "none !important",
        },
      },
    },
  },
};

// -------------------------------------------
// SAME FIELD DATA AS PlantPage
// -------------------------------------------
/* Legacy static field catalog retained only for reference. Field choices now
come from the authenticated user's /fields/mine/ endpoint.
const allFieldsData = {
  Jordan: [
    {
      id: "maru",
      name: "Maru station",
      city: "Irbid",
      west: 35.901017,
      south: 32.60608,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "mushaqar",
      name: "Mushaqar Center",
      city: "Madaba",
      west: 35.801444,
      south: 31.775077,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "rabba",
      name: "Rabba Center",
      city: "Karak",
      west: 35.742623,
      south: 31.276369,
      variety: "N/A",
      acres: "N/A",
    },
  ],
  Egypt: [
    {
      id: "sakha",
      name: "Sakha Agricultural Research Station",
      city: "Sakha",
      west: 30.9458,
      south: 31.1117,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "gemmeiza",
      name: "El Gemmiza Agricultural Research Station",
      city: "El Gemmiza",
      west: 31.028,
      south: 30.867,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "nubaria",
      name: "Nubaria Agricultural Research Station",
      city: "Nubaria",
      west: 30.66713,
      south: 30.6973,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "side",
      name: "Side Agricultural Research Station",
      city: "Side",
      west: 31.097,
      south: 29.076,
      variety: "N/A",
      acres: "N/A",
    },
  ],
  Morocco: [
    {
      id: "marchouch",
      name: "Marchouch",
      city: "Marchouch",
      west: null,
      south: null,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "sidi_alaydi",
      name: "Sidi Alaydi",
      city: "Sidi Alaydi",
      west: null,
      south: null,
      variety: "N/A",
      acres: "N/A",
    },
  ],
  Tunisia: [
    {
      id: "inrat",
      name: "INRAT experimental station",
      city: "Tunis",
      west: 10.1925,
      south: 36.8447,
      variety: "N/A",
      acres: "12.5",
    },
    {
      id: "bourabia",
      name: "Bourabia experimental station",
      city: "Bourabia",
      west: 10.1297,
      south: 36.5981,
      variety: "N/A",
      acres: "8.2",
    },
    {
      id: "chebika",
      name: "Chebika (Kairouan)",
      city: "Kairouan",
      west: 9.9358,
      south: 35.6211,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "boussalem",
      name: "Boussalem experimental station",
      city: "Boussalem",
      west: 8.9529,
      south: 36.534,
      variety: "N/A",
      acres: "N/A",
    },
    {
      id: "mornag",
      name: "Mornag experimental station",
      city: "Mornag",
      west: null,
      south: null,
      variety: "N/A",
      acres: "N/A",
    },
  ],
};
*/

export default function RecommenderSection() {

  // -------------------------------------------
  // State
  // -------------------------------------------
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const {
    fieldsByCountry,
    countries: fieldCountries,
    owners: fieldOwners,
    selectedOwnerId,
    setSelectedOwnerId,
    canSelectOwner,
    selectedOwner,
    loading: fieldsLoading,
    error: fieldsError,
  } = useMyFields();

  const [dailyHistory, setDailyHistory] = useState([]);
  const [dailyHistoryLoading, setDailyHistoryLoading] = useState(false);
  const [dailyHistoryError, setDailyHistoryError] = useState("");

  // -------------------------------------------
  // Derived
  // -------------------------------------------
  const availableFields = useMemo(
    () => fieldsByCountry[selectedCountry] || [],
    [fieldsByCountry, selectedCountry]
  );

  const currentFieldInfo = useMemo(
    () => availableFields.find((f) => f.id === selectedFieldId) || null,
    [availableFields, selectedFieldId]
  );

  const weatherHistoryData = useMemo(() => {
    return dailyHistory
      .filter((item) => item.date)
      .map((item) => ({
        date: new Date(item.date),
        air_temperature: item.air_temperature,
        rainfall: item.rainfall,
        relative_humidity: item.relative_humidity,
        soil_moisture: item.soil_moisture,
        soil_temperature: item.soil_temperature,
        electrical_conductivity: item.electrical_conductivity,
      }));
  }, [dailyHistory]);

  const soilHistoryData = useMemo(() => {
    return dailyHistory
      .filter((item) => item.date)
      .map((item) => ({
        date: new Date(item.date),
        soil_moisture: item.soil_moisture,
        soil_temperature: item.soil_temperature,
        electrical_conductivity: item.electrical_conductivity,
        air_temperature: item.air_temperature,
        rainfall: item.rainfall,
        relative_humidity: item.relative_humidity,
      }));
  }, [dailyHistory]);

  useEffect(() => {
    if (!fieldCountries.length) {
      setSelectedCountry("");
      setSelectedFieldId("");
      return;
    }

    const country = fieldCountries.includes(selectedCountry)
      ? selectedCountry
      : fieldCountries[0];
    const available = fieldsByCountry[country] || [];
    const exists = available.some((field) => field.id === selectedFieldId);

    if (country !== selectedCountry) setSelectedCountry(country);
    if (!exists) setSelectedFieldId(available[0]?.id || "");
  }, [fieldCountries, fieldsByCountry, selectedCountry, selectedFieldId]);

  // -------------------------------------------
  // Fetch backend history
  // -------------------------------------------
  const fetchDailyHistoryFromBackend = async () => {
    try {
      setDailyHistoryLoading(true);
      setDailyHistoryError("");

      if (!selectedFieldId) {
        setDailyHistory([]);
        return;
      }

      const response = await api.get("/daily-data/history/", {
        params: { field: selectedFieldId },
      });
      setDailyHistory(response.data);
    } catch (err) {
      console.error("Error fetching daily history:", err);
      setDailyHistory([]);
      setDailyHistoryError("Could not load historical data.");
    } finally {
      setDailyHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyHistoryFromBackend();
  }, [selectedCountry, selectedFieldId, currentFieldInfo]);

  // -------------------------------------------
  // Render
  // -------------------------------------------
  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      <Grid container spacing={2} direction="column" sx={{ height: "100%" }}>
        {/* LEFT SIDE — FIELD SELECTION */}
        <Grid size={12}>
          <Card
            variant="outlined"
            sx={{
              p: { xs: 1, sm: 1.5 },
              borderColor: "#e5e7eb",
              borderRadius: 2.5,
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
            }}
          >
            <CardHeader
              title="Field Selection"
              sx={{ p: 0, pb: 1 }}
              titleTypographyProps={{ fontWeight: 700, fontSize: "0.95rem" }}
            />
            <Divider sx={{ mb: 2 }} />

            {fieldsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>{fieldsError}</Alert>
            ) : !fieldsLoading && !fieldCountries.length ? (
              <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
                {selectedOwner?.is_current_user
                  ? "You have not added any fields yet. Add a field from your profile first."
                  : "This user has no public fields available."}
              </Alert>
            ) : null}

            <Grid container spacing={2} direction="column">
              {canSelectOwner ? (
                <Grid size={12}>
                  <TextField
                    fullWidth
                    select
                    label="User"
                    value={selectedOwnerId}
                    onChange={(event) => {
                      setSelectedCountry("");
                      setSelectedFieldId("");
                      setSelectedOwnerId(String(event.target.value));
                    }}
                    disabled={fieldsLoading || !fieldOwners.length}
                    size="small"
                    SelectProps={{
                      MenuProps: fieldSelectMenuProps,
                      IconComponent: KeyboardArrowDownRoundedIcon,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root fieldset": { borderColor: "#ffbf00" },
                      "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "#ffbf00" },
                      "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                        borderColor: "#ffbf00",
                        borderWidth: 2,
                      },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#ffbf00" },
                    }}
                  >
                    {fieldOwners.map((owner) => (
                      <MenuItem key={owner.id} value={String(owner.id)}>
                        {getFieldOwnerLabel(owner)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ) : null}

              <Grid size={12}>
                <TextField
                  fullWidth
                  select
                  label="Country"
                  value={selectedCountry}
                  onChange={(e) => {
                    const newCountry = e.target.value;
                    setSelectedCountry(newCountry);
                    setSelectedFieldId(fieldsByCountry[newCountry]?.[0]?.id || "");
                  }}
                  disabled={fieldsLoading || !fieldCountries.length}
                  size="small"
                  SelectProps={{
                    MenuProps: fieldSelectMenuProps,
                    IconComponent: KeyboardArrowDownRoundedIcon,
                  }}
                  sx={{
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#ffbf00",
    },
    "&:hover fieldset": {
      borderColor: "#ffbf00",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#ffbf00",
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#ffbf00",
  },
}}
                >
                  {fieldCountries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  select
                  label="Field"
                  value={selectedFieldId}
                  onChange={(e) => setSelectedFieldId(e.target.value)}
                  disabled={fieldsLoading || !availableFields.length}
                  size="small"
                  SelectProps={{
                    MenuProps: fieldSelectMenuProps,
                    IconComponent: KeyboardArrowDownRoundedIcon,
                  }}
                  sx={{
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#ffbf00",
    },
    "&:hover fieldset": {
      borderColor: "#ffbf00",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#ffbf00",
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#ffbf00",
  },
}}
                >
                  {availableFields.map((field) => (
                    <MenuItem key={field.id} value={field.id}>
                      {field.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* RIGHT SIDE — AGGREGATED DATA */}
        <Grid size={12} sx={{ flexGrow: 1 }}>
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              p: 1.5,
              borderColor: "#e5e7eb",
              borderRadius: 2.5,
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
            }}
          >
            <CardHeader
              title="Field Aggregated Data"
              titleTypographyProps={{ fontWeight: 700, fontSize: "0.95rem" }}
              action={
                <Tabs
                  value={currentTab}
                  onChange={(e, v) => setCurrentTab(v)}
                  TabIndicatorProps={{ sx: { display: "none" } }}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minHeight: 32,
                    "& .MuiTabs-flexContainer": {
                      width: { xs: "100%", sm: "auto" },
                    },
                    "& .MuiTab-root": {
                      flex: { xs: 1, sm: "initial" },
                      minHeight: 32,
                      minWidth: 0,
                      px: 1.1,
                      py: 0.5,
                      ml: { xs: 0, sm: 0.5 },
                      borderRadius: 1.5,
                      fontSize: "0.72rem",
                      textTransform: "none",
                    },
                    "& .Mui-selected": {
                      bgcolor: "#ffbf00",
                      color: "#fff !important",
                    },
                  }}
                >
                  <Tab label="On-Soil Data" />
                  <Tab label="Off-Soil Data" />
                </Tabs>
              }
              sx={{
                p: 0,
                pb: 1,
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                "& .MuiCardHeader-content": {
                  minWidth: 0,
                  width: { xs: "100%", sm: "auto" },
                },
                "& .MuiCardHeader-title": {
                  whiteSpace: "nowrap",
                },
                "& .MuiCardHeader-action": {
                  m: 0,
                  mt: { xs: 1, sm: 0 },
                  width: { xs: "100%", sm: "auto" },
                  alignSelf: { xs: "stretch", sm: "center" },
                },
              }}
            />
            <Divider />

            {/* OFF-SOIL TAB */}
            {currentTab === 1 && (
              <CardContent sx={{ p: 0, pt: 1, "&:last-child": { pb: 0 } }}>
                {dailyHistoryLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 180,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : dailyHistoryError ? (
                  <Box
                    sx={{
                      minHeight: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="error">{dailyHistoryError}</Typography>
                  </Box>
                ) : weatherHistoryData.length > 0 ? (
                  <LazySyncedMetricCharts
                    data={weatherHistoryData}
                    groupId="recommender-off-soil"
                    variant="offSoil"
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="text.secondary">
                      No off-soil measurements are available for this field yet.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            )}

            {/* ON-SOIL TAB */}
            {currentTab === 0 && (
              <CardContent sx={{ p: 0, pt: 1, "&:last-child": { pb: 0 } }}>
                {dailyHistoryLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: 180,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : dailyHistoryError ? (
                  <Box
                    sx={{
                      minHeight: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="error">{dailyHistoryError}</Typography>
                  </Box>
                ) : soilHistoryData.length > 0 ? (
                  <LazySyncedMetricCharts
                    data={soilHistoryData}
                    groupId="recommender-on-soil"
                    variant="onSoil"
                  />
                ) : (
                  <Box
                    sx={{
                      minHeight: 180,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography color="text.secondary">
                      No on-soil measurements are available for this field yet.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            )}
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
